import {
  agendarPublicacaoSchema,
  contagemFalhasPublicacaoSchema,
  copiarMidiasDePublicacaoSchema,
  criarPublicacaoSchema,
  editarPublicacaoSchema,
  exclusaoPublicacaoSchema,
  listaDePublicacoesSchema,
  perfilInstagramSchema,
  publicacaoSchema,
  statusFilaPublicacoesSchema,
  type AgendarPublicacaoDto,
  type CriarPublicacaoDto,
  type EditarPublicacaoDto,
  type ListaDePublicacoesDto,
  type PerfilInstagramDto,
  type PublicacaoDto,
  type StatusFilaPublicacoesDto,
} from "@base/contracts";
import type { ClienteHttp } from "../../../shared/http/cliente-http";

export type PublicacoesGateway = {
  listar(consulta?: {
    readonly status?: string;
    readonly soFalhas?: boolean;
    readonly de?: string;
    readonly ate?: string;
  }): Promise<ListaDePublicacoesDto>;
  calendario(de?: string, ate?: string): Promise<ListaDePublicacoesDto>;
  obter(id: string): Promise<PublicacaoDto>;
  criar(corpo: CriarPublicacaoDto): Promise<PublicacaoDto>;
  anexarMidias(id: string, arquivos: File[]): Promise<PublicacaoDto>;
  anexarMidiasDaBiblioteca(
    id: string,
    midiaIds: readonly string[],
  ): Promise<PublicacaoDto>;
  aprovar(id: string): Promise<PublicacaoDto>;
  agendar(id: string, corpo: AgendarPublicacaoDto): Promise<PublicacaoDto>;
  reprocessar(id: string): Promise<PublicacaoDto>;
  /** Edita enquanto não publicou. Com `agendarPara`, volta para a fila. */
  editar(id: string, corpo: EditarPublicacaoDto): Promise<PublicacaoDto>;
  /** Reaproveita as mídias de outro post (rede nova numa edição). */
  copiarMidias(id: string, origemId: string): Promise<PublicacaoDto>;
  /** Exclui publicacao ainda nao publicada (rascunho, agendada, na fila ou falha). */
  excluir(id: string): Promise<{ ok: true }>;
  contagemFalhas(): Promise<{ total: number }>;
  statusFila(): Promise<StatusFilaPublicacoesDto>;
  buscarPerfilInstagram(username: string): Promise<PerfilInstagramDto>;
};

export class PublicacoesGatewayHttp implements PublicacoesGateway {
  constructor(private readonly http: ClienteHttp) {}

  listar(consulta?: {
    readonly status?: string;
    readonly soFalhas?: boolean;
    readonly de?: string;
    readonly ate?: string;
  }): Promise<ListaDePublicacoesDto> {
    return this.http.requisitar("/v1/publicacoes", listaDePublicacoesSchema, {
      consulta: {
        ...(consulta?.status ? { status: consulta.status } : {}),
        ...(consulta?.soFalhas ? { soFalhas: "true" } : {}),
        ...(consulta?.de ? { de: consulta.de } : {}),
        ...(consulta?.ate ? { ate: consulta.ate } : {}),
      },
    });
  }

  calendario(de?: string, ate?: string): Promise<ListaDePublicacoesDto> {
    return this.http.requisitar(
      "/v1/publicacoes/calendario",
      listaDePublicacoesSchema,
      {
        consulta: {
          ...(de ? { de } : {}),
          ...(ate ? { ate } : {}),
        },
      },
    );
  }

  obter(id: string): Promise<PublicacaoDto> {
    return this.http.requisitar(`/v1/publicacoes/${id}`, publicacaoSchema);
  }

  criar(corpo: CriarPublicacaoDto): Promise<PublicacaoDto> {
    return this.http.requisitar("/v1/publicacoes", publicacaoSchema, {
      metodo: "POST",
      corpo: criarPublicacaoSchema.parse(corpo),
    });
  }

  anexarMidias(id: string, arquivos: File[]): Promise<PublicacaoDto> {
    const form = new FormData();
    for (const a of arquivos) form.append("arquivos", a);
    return this.http.requisitarFormData(
      `/v1/publicacoes/${id}/midias`,
      publicacaoSchema,
      form,
    );
  }

  anexarMidiasDaBiblioteca(
    id: string,
    midiaIds: readonly string[],
  ): Promise<PublicacaoDto> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}/midias-biblioteca`,
      publicacaoSchema,
      {
        metodo: "POST",
        corpo: { ids: [...midiaIds] },
      },
    );
  }

  aprovar(id: string): Promise<PublicacaoDto> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}/aprovar`,
      publicacaoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  agendar(id: string, corpo: AgendarPublicacaoDto): Promise<PublicacaoDto> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}/agendar`,
      publicacaoSchema,
      {
        metodo: "POST",
        corpo: agendarPublicacaoSchema.parse(corpo),
      },
    );
  }

  reprocessar(id: string): Promise<PublicacaoDto> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}/reprocessar`,
      publicacaoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  editar(id: string, corpo: EditarPublicacaoDto): Promise<PublicacaoDto> {
    return this.http.requisitar(`/v1/publicacoes/${id}`, publicacaoSchema, {
      metodo: "PATCH",
      corpo: editarPublicacaoSchema.parse(corpo),
    });
  }

  copiarMidias(id: string, origemId: string): Promise<PublicacaoDto> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}/midias-de-publicacao`,
      publicacaoSchema,
      {
        metodo: "POST",
        corpo: copiarMidiasDePublicacaoSchema.parse({ origemId }),
      },
    );
  }

  excluir(id: string): Promise<{ ok: true }> {
    return this.http.requisitar(
      `/v1/publicacoes/${id}`,
      exclusaoPublicacaoSchema,
      { metodo: "DELETE" },
    );
  }

  contagemFalhas(): Promise<{ total: number }> {
    return this.http.requisitar(
      "/v1/publicacoes/falhas/contagem",
      contagemFalhasPublicacaoSchema,
    );
  }

  statusFila(): Promise<StatusFilaPublicacoesDto> {
    return this.http.requisitar(
      "/v1/publicacoes/admin/fila",
      statusFilaPublicacoesSchema,
    );
  }

  buscarPerfilInstagram(username: string): Promise<PerfilInstagramDto> {
    return this.http.requisitar(
      "/v1/publicacoes/instagram/perfil",
      perfilInstagramSchema,
      { consulta: { username } },
    );
  }
}
